import { useState, useEffect, useCallback } from "react";
import { Bell, Eye, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getNotifications, markNotificationsRead } from "@/lib/api";

export default function NotificationBell() {
  const [notifications, setNotifications] = useState([]);
  const [unread, setUnread] = useState(0);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await getNotifications();
      setNotifications(res.data.notifications || []);
      setUnread(res.data.unread_count || 0);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const handleMarkRead = async () => {
    try {
      await markNotificationsRead();
      setUnread(0);
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          data-testid="notification-bell"
          variant="ghost"
          size="sm"
          className="relative text-[#8A9A86] hover:text-white hover:bg-white/10 h-8 w-8 p-0"
        >
          <Bell className="w-4 h-4" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-[#E07A5F] text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <div className="flex items-center justify-between px-3 py-2">
          <span className="text-sm font-semibold text-[#1C1917]">Notifications</span>
          {unread > 0 && (
            <button
              data-testid="mark-all-read"
              onClick={handleMarkRead}
              className="text-xs text-[#1A362D] hover:underline flex items-center gap-1"
            >
              <Check className="w-3 h-3" /> Mark all read
            </button>
          )}
        </div>
        <DropdownMenuSeparator />
        {notifications.length === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-[#78716C]">
            No notifications yet
          </div>
        ) : (
          notifications.slice(0, 10).map((n) => (
            <DropdownMenuItem key={n.id} className={`px-3 py-2.5 cursor-default ${!n.read ? "bg-[#1A362D]/5" : ""}`}>
              <div className="flex items-start gap-2 w-full">
                <Eye className="w-4 h-4 text-[#8A9A86] mt-0.5 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm ${!n.read ? "font-medium text-[#1C1917]" : "text-[#57534E]"}`}>
                    {n.message}
                  </p>
                  <p className="text-xs text-[#78716C] mt-0.5">
                    {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                  </p>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-[#1A362D] flex-shrink-0 mt-1.5" />}
              </div>
            </DropdownMenuItem>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
